/**
 * FallbackSidebar — lightweight sidebar shown when the Hub is unreachable.
 * Reproduces the fixed sidebar layout so the app doesn't shift.
 *
 * Props (same subset as the real Sidebar):
 *   - sections    (array)   Navigation sections with items
 *   - activePath  (string)  Current path
 *   - onNavigate  (function)
 *   - collapsed   (boolean)
 *   - onCollapse  (function)
 */
import { useState } from 'react';

export default function FallbackSidebar({
  sections = [],
  activePath = '/',
  onNavigate,
  collapsed: controlledCollapsed,
  onCollapse,
}) {
  const [internalCollapsed, setInternalCollapsed] = useState(false);
  const collapsed =
    controlledCollapsed !== undefined ? controlledCollapsed : internalCollapsed;

  const handleToggle = () => {
    const next = !collapsed;
    if (onCollapse) onCollapse(next);
    else setInternalCollapsed(next);
  };

  const width = collapsed ? 52 : 210;

  return (
    <>
      {/* Fixed sidebar */}
      <div
        style={{
          position: 'fixed',
          top: 48,
          left: 0,
          bottom: 0,
          zIndex: 30,
          width,
          transition: 'width 0.2s',
          background: '#1E2A40',
          borderRight: '1px solid #2F4264',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        {/* Collapse toggle */}
        <div
          style={{
            padding: collapsed ? '12px 0' : '12px 16px',
            display: 'flex',
            justifyContent: collapsed ? 'center' : 'flex-end',
          }}
        >
          <button
            onClick={handleToggle}
            style={{
              background: 'none',
              border: 'none',
              color: '#6B85A8',
              cursor: 'pointer',
              fontSize: 16,
              padding: 4,
            }}
            title={collapsed ? 'Déplier' : 'Replier'}
          >
            {collapsed ? '\u25B6' : '\u25C0'}
          </button>
        </div>

        {/* Navigation items */}
        {!collapsed &&
          sections.map((section, si) => (
            <div key={si} style={{ marginBottom: 8 }}>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: '#6B85A8',
                  letterSpacing: '0.05em',
                  padding: '8px 16px 4px',
                }}
              >
                {section.label}
              </div>
              {(section.items || []).map((item, ii) => {
                const isActive = activePath === item.path;
                return (
                  <button
                    key={ii}
                    onClick={() => onNavigate && onNavigate(item.path)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      width: '100%',
                      // Reference de position pour un eventuel lien etendu
                      // dans `label` (cf. LienNouvelOnglet dans Layout.js) :
                      // sans ceci, il se calerait sur la barre entiere.
                      position: 'relative',
                      padding: '8px 16px',
                      background: isActive
                        ? 'rgba(255,255,255,0.08)'
                        : 'transparent',
                      border: 'none',
                      borderLeft: isActive
                        ? '3px solid #4F46E5'
                        : '3px solid transparent',
                      color: isActive ? '#F1F5F9' : '#94A3B8',
                      fontSize: 13,
                      fontWeight: isActive ? 600 : 400,
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                  >
                    {item.label}
                  </button>
                );
              })}
            </div>
          ))}

        {/* Collapsed: show dots for each section item */}
        {collapsed &&
          sections.flatMap((s) => s.items || []).map((item, i) => {
            const isActive = activePath === item.path;
            return (
              <button
                key={i}
                onClick={() => onNavigate && onNavigate(item.path)}
                // `label` peut etre un element React (cf. Layout.js) : dans
                // ce cas on prend `titre`, sinon l'attribut vaudrait
                // « [object Object] ».
                title={typeof item.label === 'string' ? item.label : item.titre}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  height: 40,
                  background: isActive
                    ? 'rgba(255,255,255,0.08)'
                    : 'transparent',
                  border: 'none',
                  borderLeft: isActive
                    ? '3px solid #4F46E5'
                    : '3px solid transparent',
                  color: isActive ? '#F1F5F9' : '#94A3B8',
                  fontSize: 16,
                  cursor: 'pointer',
                }}
              >
                &bull;
              </button>
            );
          })}
      </div>

      {/* Spacer */}
      <div
        style={{
          width,
          flexShrink: 0,
          transition: 'width 0.2s',
        }}
      />
    </>
  );
}
